import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import FormulaireObjet from './FormulaireObjet/FormulaireObjet';
import Tableau from '../../../UI/tableau/Tableau';

import AttenteRequete from '../../../../hoc/AttenteRequete/AttenteRequete';
import Bouton from '../../../UI/Bouton/Bouton';
import Popup from '../../../UI/PopupVue/Popup';

import { AiOutlineClose } from 'react-icons/ai';
import { MdRefresh } from 'react-icons/md';
import { RiEditBoxFill, RiFileAddFill } from 'react-icons/ri';
import { checkMot } from '../../../../ressources/functionJS/checkMot';
import { confirmeOnClick } from '../../../../ressources/functionJS/confirmeOnClick';
import * as requestType from '../../../../ressources/glossaires/glossaire_requetes';
import * as type_btn from '../../../../ressources/glossaires/glossaire_type_de_bouton';
import * as s from '../../../../ressources/StyleGlobal/StyleGlobal';
import * as actions from '../../../../services/actions';
import styles from './FormulaireObjetsAssocieVue.module.scss';

const COLUMN_CONFIG = [
  ['bonton_supprimer', 'supprimer', 100],
  ['name', 'name', 150],
  ['viewobj_type', 'viewobj_type', 120],
  ['sensor_id', 'sensor_id', 130],
  ['positions_in_sensor_list', 'composantes_capteur', 180],
  ['get_values_onload', 'get_values_onload', 130],
  ['get_metadata_onload', 'get_metadata_onload', 140],
  ['projection_id', 'projection_id', 130],
  ['coord_x', 'coord_x', 100],
  ['coord_y', 'coord_y', 100],
  ['viewobj_path', 'viewobj_path', 200],
];

const formatViewObjType = (value) => {
  const typeMap = {
    0: 'Text',
    1: 'Capteur',
    2: 'Graphe',
    3: 'Vue',
  };
  return typeMap[value] || value;
};

const createColumn = (field, labelKey, width, deleteHandler) => {
  const baseColumn = {
    dataField: field,
    text: checkMot(labelKey),
    style: { width },
    sort: true,
  };

  if (field === 'viewobj_type') {
    return {
      ...baseColumn,
      formatter: (cell) => <strong>{formatViewObjType(cell)}</strong>,
    };
  }

  if (field === 'positions_in_sensor_list') {
    return {
      ...baseColumn,
      formatter: (cell) => (cell !== undefined ? cell.join(', ') : ''),
    };
  }

  if (field === 'bonton_supprimer') {
    return {
      dataField: field,
      text: checkMot(labelKey),
      style: { width },
      sort: false,
      formatter: (cell, row) => (
        <Bouton
          type={type_btn.danger}
          cliquer={() => deleteHandler(row.viewobj_id)}
        >
          <AiOutlineClose />
        </Bouton>
      ),
    };
  }

  return baseColumn;
};

const FormulaireObjetsAssocieVue = ({ view_id }) => {
  const dispatch = useDispatch();
  const views_objects_lists_list = useSelector(
    (state) => state.vue.views_objects_lists_list
  );

  // Local state
  const [objetSelectionne, setObjetSelectionne] = useState({});
  const [displayedData, setDisplayedData] = useState([]);

  // DataGrid columns
  // Handle delete
  const handleDelete = useCallback(
    (viewobj_id) => {
      confirmeOnClick(
        checkMot('voulez_vous_vraiment_supprimer_cette_objet'),
        () => {
          dispatch(actions.reqSupprimerObjetVue(viewobj_id));
        }
      );
    },
    [dispatch]
  );

  const columns = useMemo(
    () =>
      COLUMN_CONFIG.map(([field, labelKey, width]) =>
        createColumn(field, labelKey, width, handleDelete)
      ),
    [handleDelete]
  );

  // Fetch data on mount and when view_id changes
  useEffect(() => {
    if (view_id !== undefined) {
      dispatch(actions.reqRecupererListeObjetsAssocieVue(view_id));
    }
  }, [dispatch, view_id]);

  // Update displayed data when Redux data changes
  useEffect(() => {
    if (views_objects_lists_list[view_id] !== undefined) {
      const listeCouches = views_objects_lists_list[view_id];
      setDisplayedData(listeCouches.map((item) => ({ ...item })));
    }
  }, [views_objects_lists_list, view_id]);

  // Handle row selection
  const handleSelect = useCallback((row) => {
    if (row) {
      setObjetSelectionne(row);
    }
  }, []);

  const selectRow = useMemo(
    () => ({
      mode: 'radio',
      clickToSelect: true,
      hideSelectAll: true,
      hideSelectColumn: true,
      onSelect: (row) => {
        handleSelect(row);
      },
    }),
    [handleSelect]
  );

  const handleRefresh = useCallback(() => {
    dispatch(actions.reqRecupererListeObjetsAssocieVue(view_id));
  }, [dispatch, view_id]);

  let afficherListeCouche;
  if (views_objects_lists_list[view_id] !== undefined) {
    afficherListeCouche = (
      <div className={styles.dataGridContainer}>
        <Tableau
          donnees={displayedData}
          colonnes={columns}
          cleLigne="viewobj_id"
          selectionnerLignes={selectRow}
          hide_general_search={false}
        />
      </div>
    );
  } else {
    afficherListeCouche = (
      <Bouton cliquer={handleRefresh}>{checkMot('refresh_liste_objet')}</Bouton>
    );
  }

  return (
    <>
      <div className={s.row}>
        {/*Bouton ouvrir la popup de création d'objet de la vue*/}
        <div className={[s.col_1, styles.marginB].join(' ')}>
          <AttenteRequete
            request_type={requestType.ajouter_un_objet_associe_a_la_vue}
          >
            <Popup
              typeBoutonAffichage={type_btn.outil}
              icone={<RiFileAddFill />}
              hoverText={checkMot('ajouter_un_objet')}
            >
              {/*Ajouter une couche de vue avec formulaire de sélection ... différents type de couche*/}
              <FormulaireObjet view_id={view_id} />
            </Popup>
          </AttenteRequete>
        </div>
        <div className={[s.col_1, styles.marginB].join(' ')}>
          {/*Bouton ouvrir la popup pour modifier l'objet sélectionné dans le tableau*/}
          <AttenteRequete
            request_type={requestType.modifier_un_objet_associe_a_la_vue}
          >
            <Popup
              typeBoutonAffichage={type_btn.outil}
              desactive={JSON.stringify(objetSelectionne) === '{}'}
              icone={<RiEditBoxFill />}
              hoverText={checkMot('modifier_l_objet_selectionne')}
            >
              {/*Ajouter une couche de vue avec formulaire de sélection ... différents type de couche*/}
              <FormulaireObjet
                view_id={view_id}
                preconfigurerObjet={objetSelectionne}
              />
            </Popup>
          </AttenteRequete>
        </div>
        <div className={[s.col_1, styles.marginB].join(' ')}>
          <AttenteRequete
            request_type={
              requestType.recuperer_la_liste_des_objets_associe_a_la_vue
            }
          >
            <Bouton
              hoverText={checkMot('refresh_liste_objet')}
              type={type_btn.outil}
              cliquer={handleRefresh}
            >
              <MdRefresh />
            </Bouton>
          </AttenteRequete>
          {/*Affiche la liste des couches sous forme de liste pouvant être supprimé individuellement*/}
        </div>
      </div>
      {afficherListeCouche}
    </>
  );
};

export default FormulaireObjetsAssocieVue;
